import {
  HabilitationActionCode,
  HabilitationBlockingReasonCode,
  HabilitationDocumentRequirementNature,
  HabilitationDocumentStatus,
  HabilitationParcoursActionKind,
  HabilitationParcoursActor,
  HabilitationParcoursStatus,
  HabilitationParcoursView,
  HabilitationStepCode,
} from '@graphql/generated';
import {
  HABILITATION_BLOCKING_REASON_LABELS,
  HABILITATION_DOCUMENT_PRESENTATION,
  HABILITATION_ACTION_PRESENTATION,
  getHabilitationStepPresentation,
} from '@lib/models/demande-habilitation/habilitation-parcours-presentation';
import {
  ActeurParcoursHabilitation,
  HabilitationParcours,
  StatutCollaborationStefi,
  StatutEtapeParcours,
} from '@lib/models/demande-habilitation/habilitation-parcours.model';
import { HabilitationType } from '@lib/models/demande-habilitation/types/habilitation-type.type';

export class HabilitationParcoursMapper {
  static fromGraphQl(
    input: HabilitationParcoursView,
    type: HabilitationType,
    actor: ActeurParcoursHabilitation,
    stefiCollaboration?: StatutCollaborationStefi
  ): HabilitationParcours {
    const serverType = input.type.toLowerCase() as HabilitationType;
    if (serverType !== type) {
      throw new Error(
        `Le type serveur (${input.type}) ne correspond pas au type demandé (${type}).`
      );
    }

    const stefiActionCanExecute =
      input.etapes
        .find(step => step.code === HabilitationStepCode.CollaborationStefi)
        ?.actions.some(action => action.canExecute) ?? false;

    const etapesTousActeurs = input.etapes.map(step => {
      const presentation = getHabilitationStepPresentation(type, step.code);
      const status = this.mapStatus(step.status);
      const acteurEtape = this.mapActor(step.actor);

      return {
        ...presentation,
        acteur: acteurEtape,
        status,
        dateValidation: step.dateValidation ? new Date(step.dateValidation) : undefined,
        expanded: status !== 'terminee' && status !== 'en_attente',
        prerequisiteActions: step.prerequisiteActionCodes.map(
          code => this.getActionPresentation(code).code
        ),
        externalRequirementsSatisfied: step.externalRequirementsSatisfied,
        blockingReasons: this.mapBlockingReasons(
          step.blockingReasons.map(reason => reason.code)
        ),
        blockingReasonCodes: step.blockingReasons.map(reason => reason.code),
        sousEtapes: step.actions.map(action => {
          const actionPresentation = this.getActionPresentation(action.code);
          return {
            type: actionPresentation.code,
            kind: this.mapActionKind(action.kind),
            label: actionPresentation.label,
            buttonLabel: actionPresentation.buttonLabel,
            url: actionPresentation.url,
            status: this.mapStatus(action.status),
            canExecute: action.canExecute,
            prerequisiteActions: action.prerequisiteActionCodes.map(
              code => this.getActionPresentation(code).code
            ),
            dependentActions: action.dependentActionCodes.map(
              code => this.getActionPresentation(code).code
            ),
            externalRequirementsSatisfied: action.externalRequirementsSatisfied,
            blockingReasons: this.mapBlockingReasons(
              action.blockingReasons.map(reason => reason.code)
            ),
            blockingReasonCodes: action.blockingReasons.map(reason => reason.code),
          };
        }),
      };
    });

    return {
      type,
      canSubmit: input.canSubmit,
      submitPrerequisiteActions: input.submitPrerequisiteActionCodes.map(
        code => this.getActionPresentation(code).code
      ),
      submitExternalRequirementsSatisfied: input.submitExternalRequirementsSatisfied,
      submitBlockingReasons: this.mapBlockingReasons(
        input.submitBlockingReasons.map(reason => reason.code)
      ),
      documents: input.documents.map(document => ({
        code: this.getDocumentPresentation(document.code).code,
        nature: this.mapDocumentNature(document.nature),
        status: this.mapDocumentStatus(document.status),
      })),
      ...(stefiCollaboration
        ? {
            stefi: {
              collaboration: stefiCollaboration,
              // Un contrat généré reste consultable et le choix reste modifiable
              // tant qu'il n'est pas parti en signature. Dans les autres états,
              // le serveur décide via l'action réévaluée du parcours.
              canInteract:
                stefiCollaboration === 'contrat_en_attente' || stefiActionCanExecute,
            },
          }
        : {}),
      etapes: etapesTousActeurs.filter(etape => etape.acteur === actor),
      etapesTousActeurs,
    };
  }

  private static mapActionKind(
    kind: HabilitationParcoursActionKind
  ): 'checkbox' | 'button' | 'link' | 'programmatic_status' {
    switch (kind) {
      case HabilitationParcoursActionKind.Checkbox:
        return 'checkbox';
      case HabilitationParcoursActionKind.Button:
        return 'button';
      case HabilitationParcoursActionKind.Link:
        return 'link';
      case HabilitationParcoursActionKind.ProgrammaticStatus:
        return 'programmatic_status';
      default:
        throw new Error("Le type d'action est inconnu du front: " + String(kind));
    }
  }
  private static mapActor(actor: HabilitationParcoursActor): ActeurParcoursHabilitation {
    switch (actor) {
      case HabilitationParcoursActor.BackOffice:
        return 'shm';
      case HabilitationParcoursActor.Consultant:
        return 'consultant';
      default:
        throw new Error("L'acteur de parcours est inconnu du front: " + String(actor));
    }
  }

  private static mapStatus(status: HabilitationParcoursStatus): StatutEtapeParcours {
    switch (status) {
      case HabilitationParcoursStatus.Terminee:
        return 'terminee';
      case HabilitationParcoursStatus.EnAttente:
        return 'en_attente';
      case HabilitationParcoursStatus.Verrouillee:
        return 'verrouillee';
      case HabilitationParcoursStatus.AFaire:
        return 'a_faire';
      default:
        throw new Error('Le statut de parcours est inconnu du front: ' + String(status));
    }
  }

  private static mapBlockingReasons(reasons: HabilitationBlockingReasonCode[]): string[] {
    return reasons.map(reason => {
      const label = HABILITATION_BLOCKING_REASON_LABELS[reason];
      if (!label) {
        throw new Error(`Le code de blocage ${reason} est inconnu du front.`);
      }
      return label;
    });
  }

  private static getActionPresentation(code: HabilitationActionCode) {
    const presentation = HABILITATION_ACTION_PRESENTATION[code];
    if (!presentation) {
      throw new Error(`L action ${code} est inconnue du front.`);
    }
    return presentation;
  }

  private static getDocumentPresentation(
    code: keyof typeof HABILITATION_DOCUMENT_PRESENTATION
  ) {
    const presentation = HABILITATION_DOCUMENT_PRESENTATION[code];
    if (!presentation) {
      throw new Error(`Le document ${code} est inconnu du front.`);
    }
    return presentation;
  }

  private static mapDocumentNature(
    nature: HabilitationDocumentRequirementNature
  ): 'requis' | 'facultatif' | 'alternatif' {
    switch (nature) {
      case HabilitationDocumentRequirementNature.Requis:
        return 'requis';
      case HabilitationDocumentRequirementNature.Facultatif:
        return 'facultatif';
      case HabilitationDocumentRequirementNature.Alternatif:
        return 'alternatif';
      default:
        throw new Error('La nature de document est inconnue du front: ' + String(nature));
    }
  }

  private static mapDocumentStatus(
    status: HabilitationDocumentStatus
  ): 'manquant' | 'depose' | 'conforme' | 'non_conforme' {
    switch (status) {
      case HabilitationDocumentStatus.Manquant:
        return 'manquant';
      case HabilitationDocumentStatus.Depose:
        return 'depose';
      case HabilitationDocumentStatus.Conforme:
        return 'conforme';
      case HabilitationDocumentStatus.NonConforme:
        return 'non_conforme';
      default:
        throw new Error('Le statut de document est inconnu du front: ' + String(status));
    }
  }
}
